import React, { useState, useEffect, CSSProperties } from "react";
import { Table, OverlayTrigger, Tooltip, Badge } from "react-bootstrap";
import "jquery-confirm/css/jquery-confirm.css";
import "jquery-confirm";
import SplInstructionsModal from "./SplInstructionsModal";
import { APPOINTMENT_ACTIONS } from "../common/EventConstant";
import { RootState } from "../redux/Store";
import { useSelector } from "react-redux";
import { GetSpecialInstructionData } from "../services/SystemLocationAPI";
import {
  EDITOR_REPORT_STATUS,
  HISTORY_REPORT_STATUS,
  PICKLIST_GROUP_NAME,
  ROR_STATUS,
  TRANSCRIPTION_REPORT_STATUS,
} from "./Constant";
import { fetchPickListByGroupName } from "../services/CaseInformationApi";
import { truncateMiddleString } from "../utils/stringUtils";
import TruncateWithTooltip from "./TruncateWithTooltip";

interface DataTableProps {
  headers: {
    key: string;
    label: string;
    sortable?: boolean;
    items?: any;
    style?: any;
    isNumeric?: boolean;
  }[];
  rows?: any[];
  onAction?: (action: string, row: any) => void;
  onSortAction?: (row: any, action?: string) => void;
  onFileAction?: (action: string, row: any) => void;
  onDownloadAction?: (action: string, row: any) => void;
  on110DownloadAction?: (action: string, row: any) => void;
  isLogTable?: boolean;
  isTodayAppointmentTable?: boolean;
  isUpcomingAppointmentTable?: boolean;
  isPastAppointmentTable?: boolean;
  radioSelection?: boolean;
  onRadioSelect?: (row: any) => void; // ✅ This line
  onRowSelect?: (row: any) => void;
  selectedRow?: any;
  selectedRowRadio?: any;
  isCheckbox?: boolean;
  onCheckboxSelect?: (selectedRows: any[]) => void;
  selectedRowIds?: any;
  disabled?: boolean;
  onViewPdf?: (row: any) => void;
  toDosMainTab?: string;
  onRORPopUpAction?: (action: string, row: any) => void;
  sortBySender?: boolean;
  selectedRORData?: any;
  isRORPopUpShow?: boolean;
  checkInstruction?: boolean;
  isUserMappingTable?: boolean;
  attachmenntList?: boolean;
}

const getHistorianStatusColor = (status: string) => {
  switch (status) {
    case HISTORY_REPORT_STATUS.NOT_SCHEDULED:
      return "#FF0000";
    case HISTORY_REPORT_STATUS.PENDING:
    case HISTORY_REPORT_STATUS.SCHEDULED:
      return "#10a5ebff";
    case HISTORY_REPORT_STATUS.DRAFT:
      return "#fcaa13ff";
    case HISTORY_REPORT_STATUS.EXCEPTION:
      return "#fc7013ff";
    case HISTORY_REPORT_STATUS.COMPLETED:
      return "#0E8536";
    default:
      return "#000000";
  }
};

const getEditorStatusColor = (status: string) => {
  switch (status) {
    case EDITOR_REPORT_STATUS.OVERDUE:
    case EDITOR_REPORT_STATUS.EDITING_NOT_INITIATED:
      return "#FF0000";
    case EDITOR_REPORT_STATUS.APPROVED_BY_DOCTOR:
      return "#0092d5";
    case EDITOR_REPORT_STATUS.EDITING_PENDING:
      return "#197a72ff";
    case EDITOR_REPORT_STATUS.EDITING_IN_PROGRESS:
      return "#ffc117ff";
    case EDITOR_REPORT_STATUS.COMPLETED:
      return "#0E8536";
    default:
      return "#000000";
  }
};

const getRORStatusColor = (status: string) => {
  switch (status) {
    case ROR_STATUS.NOT_INITIATED:
      return "#fa0808ff";
    case ROR_STATUS.IN_REVIEW:
      return "#0092d5";
    case ROR_STATUS.REVIEW_COMPLETED:
      return "#0E8536";
    case ROR_STATUS.DRAFT_REVIEW:
      return "#fcaa13ff";
    default:
      return "#000000";
  }
};

const getDoctorQMEStatusColor = (status: string) => {
  switch (status) {
    case EDITOR_REPORT_STATUS.OVERDUE:
      return "#FF0000";
    case EDITOR_REPORT_STATUS.APPROVED_BY_DOCTOR:
      return "#0092D5";
    case EDITOR_REPORT_STATUS.EDITING_PENDING:
      return "#197a72ff";
    case EDITOR_REPORT_STATUS.EDITING_IN_PROGRESS:
      return "#ffc117ff";
    case EDITOR_REPORT_STATUS.SENT_FOR_REWORK:
      return "#e19217";
    case EDITOR_REPORT_STATUS.COMPLETED:
      return "#2CCF1A";
    default:
      return "#000000";
  }
};

const getTranscriptionStatusColor = (status: string) => {
  switch (status) {
    case TRANSCRIPTION_REPORT_STATUS.TRANSCRIPTION_NOT_INITIATED:
      return "#fa0808ff";
    case TRANSCRIPTION_REPORT_STATUS.TRANSCRIPTION_COMPLETED:
      return "#0E8536";
    case TRANSCRIPTION_REPORT_STATUS.TRANSCRIPTION_IN_PROGRESS:
      return "#fcaa13ff";
    case TRANSCRIPTION_REPORT_STATUS.EXCEPTION:
      return "#fc7013ff";
    case TRANSCRIPTION_REPORT_STATUS.TRANSCRIPTION_PENDING:
      return "#197a72ff";
    default:
      return "#000000";
  }
};

const DataTable: React.FC<DataTableProps> = ({
  headers,
  rows = [],
  onAction,
  onSortAction,
  onFileAction,
  onDownloadAction,
  on110DownloadAction,
  isLogTable = false,
  isTodayAppointmentTable,
  isUpcomingAppointmentTable,
  isPastAppointmentTable,
  radioSelection = false,
  onRadioSelect,
  onRowSelect,
  selectedRow,
  isCheckbox = false,
  onCheckboxSelect,
  selectedRowIds = [],
  disabled = false,
  onViewPdf,
  toDosMainTab,
  onRORPopUpAction,
  sortBySender = false,
  selectedRORData = {},
  isRORPopUpShow = false,
  checkInstruction = false,
  isUserMappingTable = false,
  attachmenntList = false,
}) => {
  const userID = useSelector((state: RootState) => state.auth.userid);

  const [sortedRows, setSortedRows] = useState<any[]>(rows);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [instructionOption, setInstructionOption] = useState<
    { value: string; label: string }[]
  >([]);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "ASC" | "DESC" | null;
  }>({
    key: "",
    direction: null,
  });

  const tableStyle = {
    border: "1px solid #ddd",
    borderCollapse: "collapse" as const,
    width: "100%",
    tableLayout: "fixed",
  };

  const thStyle: CSSProperties = {
    border: "none",
    padding: "8px",
    backgroundColor: "#e0f7fa",
    color: "#000",
    cursor: "pointer",
    position: "sticky",
    top: 0,
    zIndex: -90,
  };

  const tdStyle = {
    border: "none",
    padding: "8px",
  };

  const handleSort = (key: string, direction: "ASC" | "DESC") => {
    if (sortConfig.key === key && sortConfig.direction === direction) {
      return;
    }
    const sorted = [...sortedRows].sort((a, b) => {
      if (direction === "ASC") {
        return a[key] > b[key] ? 1 : -1;
      }
      if (direction === "DESC") {
        return a[key] < b[key] ? 1 : -1;
      }
      return 0;
    });

    setSortedRows(sorted);
    setSortConfig({ key, direction });
    onSortAction?.({ key, direction });
  };

  useEffect(() => {
    if (
      isCheckbox &&
      rows?.length > 0 &&
      selectedRowIds?.length > 0 &&
      Array.isArray(selectedRowIds)
    ) {
      const cleanedIds = selectedRowIds?.map((id: string) => id?.trim());
      const selectedIdSet = new Set(cleanedIds);

      const selected = rows?.filter((row) => selectedIdSet.has(String(row.id)));

      const unselected = rows?.filter(
        (row) => !selectedIdSet.has(String(row.id))
      );

      const ordered = [...selected, ...unselected];

      setSortedRows(ordered);
      setSelectedRows(selected);
      const allSelected = rows?.length > 0 && selected?.length === rows?.length;
      setSelectAll(allSelected);
    } else {
      setSortedRows(rows || []);
      setSelectedRows([]);
    }
  }, [rows]);

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    const updated = newSelectAll ? [...sortedRows] : [];
    setSelectedRows(updated);
    setSelectAll(newSelectAll);
    onCheckboxSelect?.(updated);
  };

  const handleCheckboxChange = (row: any) => {
    const isSelected = selectedRows.some((r) => r.id === row.id);
    const updated = isSelected
      ? selectedRows.filter((r) => r.id !== row.id)
      : [...selectedRows, row];
    setSelectedRows(updated);
    setSelectAll(updated.length === sortedRows.length);
    onCheckboxSelect?.(updated);
  };
  const allowedFeatures = useSelector(
    (state: RootState) => state.featureAccess.allowedFeatures
  );

  const is110Download = allowedFeatures.has(
    APPOINTMENT_ACTIONS.GENERATE_110_POS_SIBTF_FORM
  );
  const [showModal, setShowModal] = useState(false);
  const [instructionsData, setInstructionsData] = useState<any[]>([]);
  const [modalPos, setModalPos] = useState<{
    top: number;
    left?: number;
    right?: number;
  } | null>(null);

  const params = {
    location: {
      groupName: PICKLIST_GROUP_NAME.Location_SplInstructions,
      triggerById: userID,
    },
  };

  const fetchCategoryData = async () => {
    try {
      const categoryData = await fetchPickListByGroupName(params.location);
      setInstructionOption(categoryData);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    if (checkInstruction) fetchCategoryData();
  }, [checkInstruction]);

  const handleOpenModal = (dataString: string, e: React.MouseEvent) => {
    try {
      const parsedData = JSON.parse(dataString);

      setInstructionsData(parsedData);
    } catch (e) {
      console.error("Invalid JSON for splInstructions", e);
      setInstructionsData([]);
    }

    // get clicked icon position
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    // since modal width is 50%
    const modalWidth = window.innerWidth * 0.5;
    // small space below icon
    const verticalOffset = 10;
    // new slight horizontal offset
    const horizontalOffset = 40;

    let position: { top: number; left?: number; right?: number };

    // Check if the icon is on left or right side of screen
    if (rect.left < window.innerWidth / 2) {
      // icon on left side → open modal slightly to the right
      position = {
        top: rect.bottom + verticalOffset,
        left: rect.left + horizontalOffset,
      };
    } else {
      // icon on right side → open modal slightly to the left
      position = {
        top: rect.bottom + verticalOffset,
        left: rect.right - modalWidth - horizontalOffset,
      };
    }

    setModalPos(position);
    setShowModal(true);
  };

  const handleOpenInstructionModal = async (
    medicareID: any,
    e: React.MouseEvent
  ) => {
    try {
      if (medicareID) {
        const instruction = await GetSpecialInstructionData(medicareID);

        if (Array.isArray(instruction) && instruction.length > 0) {
          const transformed = instruction.map((item: any) => ({
            ...item,
            SplInstructionName: item.splInstructionName,
            SplInstructionData: item.splInstructionData,
          }));

          setInstructionsData(transformed);
        } else {
          const optionTransformed = instructionOption.map((item: any) => ({
            ...item,
            SplInstructionName: item.label,
            SplInstructionData: "-",
          }));

          setInstructionsData(optionTransformed);
        }
      }
    } catch (e) {
      console.error("Invalid JSON for splInstructions", e);
      setInstructionsData([]);
    }

    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const modalWidth = window.innerWidth * 0.5;
    const verticalOffset = 5;
    const horizontalOffset = 18;
    let position: { top: number; left?: number; right?: number };
    if (rect.left < window.innerWidth / 2) {
      position = {
        top: rect.bottom + verticalOffset,
        left: rect.left + horizontalOffset,
      };
    } else {
      position = {
        top: rect.bottom + verticalOffset,
        left: rect.right - modalWidth - horizontalOffset,
      };
    }

    setModalPos(position);
    setShowModal(true);
  };

  const getColorByColumn = (key: any, value: any) => {
    if (key === "rorStatusName") return getRORStatusColor(value);
    if (key === "editorStatusName") return getEditorStatusColor(value);
    if (key === "historianStatusName") return getHistorianStatusColor(value);
    if (key === "doctorQMEStatusName") return getDoctorQMEStatusColor(value);
    if (key === "transcriberStatusName") return getTranscriptionStatusColor(value);
    return undefined;
  };

  const renderTooltipText = (value: string, rowId: number) => (
    <span
      style={{
        whiteSpace: "normal",
        wordBreak: "break-word",
        display: "block",
      }}
    >
      {value}
    </span>
  );

  return (
    <>
      <div className={isUserMappingTable ? "table-responsiveNewSet" : ""}>
        <div className="table-responsive border-0 mb-2">
          <Table style={{ tableLayout: "fixed" }}>
            <thead>
              <tr>
                {radioSelection && <th style={{ width: "5%" }} />}

                {isCheckbox && (
                  <th style={{ width: "5%", textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      disabled={disabled}
                    />
                  </th>
                )}

                {headers.map((header) => (
                  <th
                    key={header.key}
                    style={{
                      border: "none",
                      padding: "8px",
                      backgroundColor: "#e0f7fa",
                      color: "#000",
                      cursor: header.sortable ? "pointer" : "default",
                      position: "sticky",
                      top: 0,
                      zIndex: -90,

                      textAlign: [
                        "isActiveDetails",
                        "appointmentStatusDesc",
                        "actions",
                        "phonenumber",
                        "download",
                        "downloadFile",
                      ].includes(header.key)
                        ? "center"
                        : "left",
                      ...header.style,
                    }}
                  >
                    <div
                      style={{
                        display: [
                          "isActiveDetails",
                          "appointmentStatusDesc",
                          "actions",
                          "phonenumber",
                          "contactphonenumber",
                          "download",
                          "downloadFile",
                        ].includes(header.key)
                          ? "flex"
                          : "flex",

                        justifyContent: [
                          "isActiveDetails",
                          "appointmentStatusDesc",
                          "actions",
                          "phonenumber",
                          "contactphonenumber",
                          "download",
                          "downloads",
                          "downloadFile",
                        ].includes(header.key)
                          ? "space-evenly"
                          : "space-between",

                        alignItems: "center",
                      }}
                    >
                      <span>{header.label}</span>
                      {header.sortable && (
                        <span
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            marginLeft: "4px",
                            cursor: "pointer",
                            position: "absolute",
                            right: "0px",
                          }}
                        >
                          {!(
                            sortConfig.key === header.key &&
                            sortConfig.direction === "ASC"
                          ) && (
                              <span
                                className="material-icons"
                                style={{
                                  fontSize: "18px",
                                  lineHeight: "10px",
                                }}
                                onClick={() => handleSort(header.key, "ASC")}
                              >
                                arrow_drop_up
                              </span>
                            )}
                          {!(
                            sortConfig.key === header.key &&
                            sortConfig.direction === "DESC"
                          ) && (
                              <span
                                className="material-icons"
                                style={{
                                  fontSize: "18px",
                                  lineHeight: "10px",
                                }}
                                onClick={() => handleSort(header.key, "DESC")}
                              >
                                arrow_drop_down
                              </span>
                            )}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRows?.length > 0 ? (
                sortedRows.map((row, index) => (
                  <tr
                    key={row.id}
                    style={{
                      backgroundColor: index % 2 === 1 ? "white" : "#f2f2f2",
                    }}
                  >
                    {radioSelection && (
                      <td style={{ textAlign: "center" }}>
                        <input
                          type="radio"
                          name="rowSelect"
                          checked={
                            selectedRow?.original?.scheduleId ===
                            row.original?.scheduleId
                          }
                          onChange={() => {
                            onRadioSelect?.(row);
                          }}
                        />
                      </td>
                    )}

                    {isCheckbox && (
                      <td style={{ textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={selectedRows.some((r) => r.id === row.id)}
                          onChange={() => handleCheckboxChange(row)}
                          disabled={disabled}
                        />
                      </td>
                    )}

                    {headers.map((header) => {
                      if (header.key === "isActiveDetails") {
                        return (
                          <td
                            key={header.key}
                            className="text-center"
                            style={{
                              color:
                                row.isActiveDetails === "Yes"
                                  ? "#0d6efd"
                                  : "#EE3339",
                              textAlign: "center",
                            }}
                          >
                            {row.isActiveDetails}
                          </td>
                        );
                      }

                      if (header.key === "appointmentStatusDesc") {
                        return (
                          <td
                            key={header.key}
                            className="text-center"
                            style={{
                              textAlign: "center",
                            }}
                          >
                            {row.appointmentStatusDesc}
                          </td>
                        );
                      }

                      if (header.key === "documentTypeName" && !attachmenntList) {
                        const documentTypeName = row?.documentTypeName;
                        const latestPosition = row?.latestPosition;

                        return (
                          <td key={header.key}>
                            <div>
                              <TruncateWithTooltip>
                                <span>{documentTypeName}</span>
                              </TruncateWithTooltip>
                              {latestPosition && (
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip id="tooltip-draft">
                                      {`Draft ${latestPosition}`}
                                    </Tooltip>
                                  }
                                >
                                  <span
                                    className="ms-1 fw-medium"
                                    style={{
                                      color: "#4f46e5",
                                      fontSize: "0.8rem",
                                      cursor: "pointer",
                                    }}
                                  >
                                    ({latestPosition})
                                  </span>
                                </OverlayTrigger>
                              )}
                            </div>
                          </td>
                        );
                      }

                      if (header.key === "fileAction") {
                        if (
                          row.documentCategory ==
                          "110 Form (QME Appointment Notification)"
                        ) {
                          return (
                            <td key={header.key} style={{ padding: "8px" }}>
                              <span
                                className="material-icons color_brand"
                                onClick={() =>
                                  onDownloadAction?.(row, "download")
                                }
                                style={{ cursor: "pointer" }}
                              >
                                download
                              </span>
                            </td>
                          );
                        } else {
                          return (
                            <td key={header.key} style={{ padding: "8px" }}>
                              {row.fileAction && (
                                <>
                                  <span
                                    className="attachfile d-inline-flex align-items-center"
                                    style={{ cursor: "pointer" }}
                                    onClick={() =>
                                      onFileAction?.(row, "fileAction")
                                    }
                                    role="button"
                                  >
                                    <span className="material-icons mx-1 color_brand text-dark">
                                      attachment
                                    </span>
                                    {row.fileAction}
                                  </span>
                                </>
                              )}
                            </td>
                          );
                        }
                      }
                      if (header.key === "fileCount") {
                        return (
                          <td key={header.key} style={{ padding: "8px" }}>
                            {row.fileCount === 0 ? (
                              "-"
                            ) : (
                              <span className="attachfile">
                                <span
                                  className="material-icons mx-1 color_brand text-dark"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => onRowSelect?.(row.keyId)}
                                >
                                  attachment
                                </span>
                                {row.fileCount}
                              </span>
                            )}
                          </td>
                        );
                      }

                      if (header.key === "countOfFiles") {
                        return (
                          <td key={header.key} style={{ padding: "8px" }}>
                            {row.countOfFiles === 0 ? (
                              "-"
                            ) : (
                              <span className="attachfile">
                                <span
                                  className="material-icons mx-1 color_brand text-dark"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => onRowSelect?.(row.noteId)}
                                >
                                  attachment
                                </span>
                                {row.countOfFiles}
                              </span>
                            )}
                          </td>
                        );
                      }

                      if (header.key === "documentTypeName" && attachmenntList) {
                        const hasCategoryHeader = headers.some(
                          (h) => h.label === "Document Category"
                        );
                        const tooltipContent = hasCategoryHeader
                          ? row.senderKeyName
                          : row.documentCategory;

                        return (
                          <td key={header.key} style={{ padding: "8px" }}>
                            <div
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              {row.documentTypeName}
                              <OverlayTrigger
                                placement="auto"
                                delay={{ show: 200, hide: 10 }}
                                overlay={
                                  <Tooltip id={`tooltip-info-${row.id}`}>
                                    {tooltipContent}
                                  </Tooltip>
                                }
                              >
                                <span
                                  className="material-symbols-outlined"
                                  style={{
                                    cursor: "pointer",
                                    color: "#f39c12",
                                    fontSize: "18px",
                                    marginLeft: "8px",
                                    verticalAlign: "middle",
                                  }}
                                >
                                  info
                                </span>
                              </OverlayTrigger>
                            </div>
                          </td>
                        );
                      }



                      if (header.key === "fileNameNotes") {
                        const cellContent = row.fileName;

                        return (
                          <td key={header.key}>
                            <span
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <OverlayTrigger
                                placement="auto"
                                delay={{ show: 200, hide: 10 }}
                                overlay={
                                  cellContent ? (
                                    <Tooltip
                                      id={`tooltip-${header.key}-${row.id}`}
                                    >
                                      {cellContent}
                                    </Tooltip>
                                  ) : (
                                    <></>
                                  )
                                }
                              >
                                <span
                                  className="d-inline-block text-truncate"
                                  style={{
                                    maxWidth: "100%",
                                    cursor: "pointer",
                                  }}
                                >
                                  {cellContent && cellContent.length > 20
                                    ? truncateMiddleString(cellContent)
                                    : cellContent}
                                </span>
                              </OverlayTrigger>

                              {row.noOfPages > 0 && (
                                <span
                                  style={{ color: "#0098d7", marginLeft: "5px" }}
                                >
                                  {row.noOfPages} pages
                                </span>
                              )}
                            </span>
                          </td>
                        );
                      }
                      if (header.key === "fileName" && attachmenntList) {
                        const cellContent = row.fileName;

                        return (
                          <td key={header.key}>
                            <span
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <OverlayTrigger
                                placement="auto"
                                delay={{ show: 200, hide: 10 }}
                                overlay={
                                  cellContent ? (
                                    <Tooltip
                                      id={`tooltip-${header.key}-${row.id}`}
                                    >
                                      {cellContent}
                                    </Tooltip>
                                  ) : (
                                    <></>
                                  )
                                }
                              >
                                <span
                                  className="d-inline-block text-truncate"
                                  style={{
                                    maxWidth: "100%",
                                    cursor: "pointer",
                                  }}
                                >
                                  {cellContent && cellContent.length > 20
                                    ? truncateMiddleString(cellContent)
                                    : cellContent}
                                </span>
                              </OverlayTrigger>

                              {row.noOfPages > 0 && (
                                <span
                                  style={{ color: "#0098d7", marginLeft: "5px" }}
                                >
                                  {row.noOfPages} pages
                                </span>
                              )}
                            </span>
                          </td>
                        );
                      }

                      if (
                        header.key === "isEmailNotification" ||
                        header.key === "isSmsNotification" ||
                        header.key === "isInAppNotification"
                      ) {
                        return (
                          <td
                            key={header.key}
                            style={{ padding: "8px", textAlign: "center" }}
                          >
                            <input
                              type="checkbox"
                              checked={row[header.key] === 1}
                              onChange={() =>
                                onDownloadAction?.(row.id, header.key)
                              }
                            />
                          </td>
                        );
                      }

                      if (header.key === "download") {
                        if (
                          row.documentCategory ==
                          "110 Form (QME Appointment Notification)"
                        ) {
                          return (
                            <td key={header.key} style={{ padding: "8px" }}>
                              <span
                                className="material-icons color_brand"
                                onClick={() =>
                                  on110DownloadAction?.(row, "download")
                                }
                                style={{ cursor: "pointer" }}
                              >
                                download
                              </span>
                            </td>
                          );
                        } else {
                          return (
                            <td key={header.key} style={{ padding: "8px" }}>
                              {row.download && (
                                <span
                                  className="material-icons color_brand"
                                  onClick={() =>
                                    onDownloadAction?.(row, "download")
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  download
                                </span>
                              )}
                            </td>
                          );
                        }
                      }
                      if (header.key === "downloadFile") {
                        const fileUrl = row.downloadFile;
                        const isPdf = fileUrl?.toLowerCase().endsWith(".pdf");
                        const isImage =
                          /\.(png|jpe?g|gif|tiff?|heif|heic|webp)$/i.test(
                            fileUrl || ""
                          );

                        return (
                          <td key={header.key} style={{ padding: "8px" }}>
                            {row.downloadFile && (
                              <>
                                {(isPdf || isImage) && (
                                  <span
                                    className="material-icons material-symbols-outlined color_brand mx-1"
                                    onClick={() => onViewPdf?.(row)}
                                    style={{ cursor: "pointer" }}
                                    title="View File"
                                  >
                                    document_search
                                  </span>
                                )}
                                <span
                                  className="material-icons color_brand mx-2"
                                  onClick={() =>
                                    onDownloadAction?.(row, "download")
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  download
                                </span>
                              </>
                            )}
                          </td>
                        );
                      }

                      if (header.key === "downloads" && is110Download) {
                        return (
                          <td key={header.key} style={{ padding: "8px" }}>
                            {row.download && row.status != "Cancelled" && (
                              <span
                                className="material-icons color_brand"
                                onClick={() =>
                                  onDownloadAction?.(row, "download")
                                }
                                style={{ cursor: "pointer" }}
                              >
                                download
                              </span>
                            )}
                          </td>
                        );
                      }
                      if (header.key === "activityDescription") {
                        const activityText = row.activityDescription || "";
                        const showTooltip =
                          row.sourceType === "Notes" && activityText.length > 30;
                        const style: React.CSSProperties =
                          row.sourceType === "Activity"
                            ? {
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              width: "87%",
                              display: "inline-block",
                            }
                            : {
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              width: "87%",
                              display: "inline-block",
                            };

                        const content = (
                          <span
                            style={style}
                            dangerouslySetInnerHTML={{
                              __html: row.activityDescription || "",
                            }}
                          />
                        );

                        return (
                          <td key={header.key} style={{ padding: "8px" }}>
                            {showTooltip ? (
                              <OverlayTrigger
                                placement="auto"
                                delay={{ show: 200, hide: 10 }}
                                overlay={
                                  <Tooltip
                                    id={`view-tooltip-${row.id}`}
                                    style={{ backgroundColor: "#b3e0ff" }}
                                  >
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html: row.activityDescription || "",
                                      }}
                                    />
                                  </Tooltip>
                                }
                              >
                                {content}
                              </OverlayTrigger>
                            ) : (
                              content
                            )}
                          </td>
                        );
                      }

                      if (header.key === "sourceType") {
                        return (
                          <td key={header.key} style={{ padding: "8px" }}>
                            {row.sourceType === "Notes" ? (
                              <span
                                style={{
                                  color: "#1976d2",
                                  border: "1px solid #0092d5",
                                  borderRadius: "10%",
                                  padding: "10px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "25px",
                                  height: "25px",
                                  fontSize: "18px",
                                }}
                                title="Notes"
                                className="material-symbols-outlined"
                              >
                                draw
                              </span>
                            ) : row.sourceType === "Activity" ? (
                              <span
                                style={{
                                  color: "#d32f2f",
                                  border: "1px solid #dc3545",
                                  borderRadius: "10%",
                                  padding: "10px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "25px",
                                  height: "25px",
                                  fontSize: "18px",
                                }}
                                title="Activity"
                                className="material-symbols-outlined"
                              >
                                manage_history
                              </span>
                            ) : null}
                          </td>
                        );
                      }

                      if (
                        header.key === "locationcount" &&
                        row?.locationcount > 0
                      ) {
                        const cellContent = row[header.key];
                        return (
                          <td
                            key={header.key}
                            style={{ padding: "8px", textAlign: "left" }}
                          >
                            <div
                              style={{
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "8px",
                              }}
                            >
                              <OverlayTrigger
                                placement="auto"
                                delay={{ show: 200, hide: 10 }}
                                overlay={
                                  cellContent && cellContent?.length > 15 ? (
                                    <Tooltip
                                      id={`tooltip-${header.key}-${row.id}`}
                                      style={{ backgroundColor: "#b3e0ff" }}
                                    >
                                      {cellContent}
                                    </Tooltip>
                                  ) : (
                                    <></>
                                  )
                                }
                              >
                                <span
                                  className="d-inline-block text-truncate"
                                  style={{
                                    maxWidth: "calc(100% - 28px)",
                                    cursor: "pointer",
                                    float: "left",
                                    width: "35px",
                                  }}
                                >
                                  {cellContent}
                                </span>
                              </OverlayTrigger>

                              <span
                                className="material-symbols-outlined"
                                style={{ cursor: "pointer", color: "#007bff" }}
                                onClick={() =>
                                  onDownloadAction?.(row, "download")
                                }
                              >
                                <img
                                  src="/map.png"
                                  alt="map"
                                  className="bounce-small"
                                  style={{
                                    width: "23px",
                                    height: "21px",
                                    float: "left",
                                  }}
                                />
                              </span>
                            </div>
                          </td>
                        );
                      }
                      if (header.key === "emailid" || header.key === "contactemailaddress" || header.key === "emailId" || header.key === "emailAddress" || header.key === "emailaddress" || header.key === "sharedWithEmails") {
  const email = row[header.key];

   return (
    <td
      key={header.key}
      style={{ padding: "8px", textAlign: "left" }}
    >
      {email ? (
        <a
          href={`mailto:${email}`}
          title={email} // Tooltip on hover
          style={{
            color: "#0d6efd",
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          {email.length > 15
            ? `${email.substring(0, 15)}...`
            : email}
        </a>
      ) : (
        ""
      )}
    </td>
  );
}
                      if (header.key === "providerAddress") {
                        const cellContent = row[header.key];
                        return (
                          <td
                            key={header.key}
                            style={{ padding: "8px", textAlign: "left" }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "8px",
                              }}
                            >
                              <span
                                className="material-symbols-outlined d-flex"
                                style={{
                                  cursor: "pointer",
                                  color: "#007bff",
                                  width: "25px",
                                }}
                                onClick={() =>
                                  onDownloadAction?.(row, "download")
                                }
                              >
                                <img
                                  src="/map.png"
                                  alt="map"
                                  className="bounce-small"
                                  style={{ width: "23px", height: "21px" }}
                                />
                              </span>

                              <OverlayTrigger
                                placement="auto"
                                delay={{ show: 200, hide: 10 }}
                                overlay={
                                  cellContent && cellContent?.length > 15 ? (
                                    <Tooltip
                                      id={`tooltip-${header.key}-${row.id}`}
                                      style={{ backgroundColor: "#b3e0ff" }}
                                    >
                                      {cellContent}
                                    </Tooltip>
                                  ) : (
                                    <></>
                                  )
                                }
                              >
                                <span
                                  className="d-flex text-truncate"
                                  style={{
                                    maxWidth: "calc(100% - 28px)",
                                    cursor: "pointer",
                                    width: "95%",
                                  }}
                                >
                                  {cellContent}
                                </span>
                              </OverlayTrigger>
                            </div>
                          </td>
                        );
                      }

                      if (header.key === "providerAddressLocation") {
                        const cellContent = row[header.key];
                        return (
                          <td
                            key={header.key}
                            style={{ padding: "8px", textAlign: "left" }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "8px",
                              }}
                            >
                              <OverlayTrigger
                                placement="auto"
                                delay={{ show: 200, hide: 10 }}
                                overlay={
                                  cellContent && cellContent?.length > 15 ? (
                                    <Tooltip
                                      id={`tooltip-${header.key}-${row.id}`}
                                      style={{ backgroundColor: "#b3e0ff" }}
                                    >
                                      {cellContent}
                                    </Tooltip>
                                  ) : (
                                    <></>
                                  )
                                }
                              >
                                <span
                                  className="d-inline-block text-truncate"
                                  style={{
                                    maxWidth: "calc(100% - 28px)",
                                    cursor: "pointer",
                                  }}
                                >
                                  {cellContent}
                                </span>
                              </OverlayTrigger>

                              <span
                                className="material-symbols-outlined"
                                style={{ cursor: "pointer", color: "#007bff" }}
                                onClick={() =>
                                  onDownloadAction?.(row, "download")
                                }
                              >
                                <img
                                  src="/map.png"
                                  alt="map"
                                  className="bounce-small"
                                  style={{ width: "23px", height: "21px" }}
                                />
                              </span>
                            </div>
                          </td>
                        );
                      }

                      if (header.key === "specialInstructions") {
                        return (
                          <td key={header.key} style={{ textAlign: "center" }}>
                            <span
                              className="material-symbols-outlined"
                              style={{
                                cursor: "pointer",
                                color: "#f39c12",
                                fontSize: "20px",
                              }}
                              onClick={(e) => handleOpenModal(row[header.key], e)}
                            >
                              info
                            </span>
                          </td>
                        );
                      }
                      if (
                        header.key === "locationOfExam" ||
                        header.key === "providerName"
                      ) {
                        return (
                          <td key={header.key}>
                            {header.key === "locationOfExam"
                              ? row.locationOfExam
                              : row.providerName}
                            {row.medicareId ? (
                              <span
                                className="material-symbols-outlined"
                                style={{
                                  cursor: "pointer",
                                  color: "#f39c12",
                                  fontSize: "18px",
                                  marginLeft: "8px",
                                  verticalAlign: "middle",
                                }}
                                onClick={(e) =>
                                  handleOpenInstructionModal(row.medicareId, e)
                                }
                              >
                                info
                              </span>
                            ) : null}
                          </td>
                        );
                      }
                      if (header.key === "medicareProviderName") {
                        return (
                          <td key={header.key}>
                            {row.medicareProviderName}
                            {row?.original?.medicareProviderId ? (
                              <span
                                className="material-symbols-outlined"
                                style={{
                                  cursor: "pointer",
                                  color: "#f39c12",
                                  fontSize: "18px",
                                  marginLeft: "8px",
                                  verticalAlign: "middle",
                                }}
                                onClick={(e) =>
                                  handleOpenInstructionModal(
                                    row.original.medicareProviderId,
                                    e
                                  )
                                }
                              >
                                info
                              </span>
                            ) : null}
                          </td>
                        );
                      }

                      if (header.key === 'Attachmentactions') {
                        const fileUrl = row.fileName;
                        const isPdf = fileUrl?.toLowerCase().endsWith('.pdf');
                        const isImage =
                          /\.(png|jpe?g|gif|tiff?|heif|heic|webp)$/i.test(
                            fileUrl || '',
                          );

                        return (
                          <td key={header.key} style={{ padding: '8px' }}>
                            {fileUrl && (
                              <>
                                {(isPdf || isImage) && (
                                  <span
                                    className='material-icons material-symbols-outlined color_brand mx-1'
                                    onClick={() => onViewPdf?.(row)} // Unified viewer handler
                                    style={{ cursor: 'pointer' }}
                                    title='View File'
                                  >
                                    document_search
                                  </span>
                                )}
                                <span
                                  className='material-icons color_brand mx-1'
                                  onClick={() =>
                                    onDownloadAction?.(row, 'download')
                                  }
                                  style={{ cursor: 'pointer' }}
                                >
                                  download
                                </span>
                              </>
                            )}
                          </td>
                        );
                      }

                      if (
                        header.key === "documentTypeName" &&
                        toDosMainTab === "Record_Review" &&
                        (isRORPopUpShow ||
                          selectedRORData.rorStatus ===
                          ROR_STATUS.REVIEW_COMPLETED ||
                          selectedRORData.rorStatus ===
                          ROR_STATUS.Review_Completed_Delayed) &&
                        row.documentTypeName === "Record Review Summary"
                      ) {
                        const cellContent = row[header.key];
                        return (
                          <td
                            key={header.key}
                            style={{ padding: "8px", textAlign: "left" }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "8px",
                              }}
                            >
                              <OverlayTrigger
                                placement="auto"
                                delay={{ show: 200, hide: 10 }}
                                overlay={
                                  cellContent && cellContent?.length > 15 ? (
                                    <Tooltip
                                      id={`tooltip-${header.key}-${row.id}`}
                                      style={{ backgroundColor: "#b3e0ff" }}
                                    >
                                      {cellContent}
                                    </Tooltip>
                                  ) : (
                                    <></>
                                  )
                                }
                              >
                                <span
                                  className="d-inline-block text-truncate"
                                  style={{
                                    maxWidth: "calc(100% - 28px)",
                                    cursor: "pointer",
                                  }}
                                >
                                  {cellContent}
                                </span>
                              </OverlayTrigger>

                              <span
                                onClick={() => onRORPopUpAction?.(row, "popUp")}
                              >
                                <img
                                  src="/ROR.png"
                                  alt="map"
                                  className="bounce-small"
                                  style={{
                                    width: "23px",
                                    height: "21px",
                                    float: "left",
                                    cursor: "pointer",
                                  }}
                                />
                              </span>
                            </div>
                          </td>
                        );
                      }

                      if (header.key === "actions") {
                        return (
                          <td
                            key={header.key}
                            style={{ padding: "8px", textAlign: "center" }}
                          >
                            {row.actions?.View && (
                              <OverlayTrigger
                                placement="auto"
                                delay={{ show: 200, hide: 10 }}
                                overlay={
                                  <Tooltip
                                    id={`view-tooltip-${row.id}`}
                                    style={{ backgroundColor: "#b3e0ff" }}
                                  >
                                    View
                                  </Tooltip>
                                }
                              >
                                <span
                                  className="material-icons color_brand"
                                  onClick={() => onAction?.(row, "View")}
                                  style={{ cursor: "pointer" }}
                                >
                                  visibility
                                </span>
                              </OverlayTrigger>
                            )}
                            {row.actions?.Edit && (
                              <OverlayTrigger
                                placement="auto"
                                delay={{ show: 200, hide: 10 }}
                                overlay={
                                  <Tooltip
                                    id={`edit-tooltip-${row.id}`}
                                    style={{ backgroundColor: "#b3e0ff" }}
                                  >
                                    Edit
                                  </Tooltip>
                                }
                              >
                                <span
                                  className="material-icons color_brand"
                                  onClick={() => onAction?.(row, "Edit")}
                                  style={{ cursor: "pointer" }}
                                >
                                  edit
                                </span>
                              </OverlayTrigger>
                            )}
                          </td>
                        );
                      }
                      if (header.key === "subjectFullName" && row.isRead == "0") {
                        return (
                          <td
                            key={header.key}
                            style={{
                              cursor: "pointer",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span onClick={() => onAction?.(row, "CaseIdClick")}>
                              {row.subjectFullName}
                            </span>
                            <img
                              src="/foldergif.gif"
                              height="38px"
                              width="38px"
                              alt="Folder"
                              style={{ float: "right", marginRight: "10px" }}
                            />
                          </td>
                        );
                      }

                      const dateNextLineFields = ["userMappingUploadedOnBy"];
                      if (dateNextLineFields.includes(header.key)) {
                        const datePart = row?.userMappingUploadedOnBy;
                        const byPart = row?.modifiedByName;

                        return (
                          <td style={{ verticalAlign: " top !important;" }}>
                            <div style={{ display: "flex", flexDirection: "column", cursor: "default", }}>
                              <span style={{ fontWeight: 500 }}>{datePart}</span>
                              <TruncateWithTooltip>
                                <span
                                  style={{
                                    fontSize: "13px",
                                    color: "#726d6d",
                                  }}
                                >
                                  {byPart}
                                </span>
                              </TruncateWithTooltip>
                            </div>
                          </td>
                        );
                      }

                      const nextLineFields = ["mappedUser", "mappedRole", "roleMapping", "userMapping"];
                      if (nextLineFields.includes(header.key)) {
                        return (
                          <td key={header.key}>
                            <span
                              style={{
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                display: "block",
                                cursor: "default",
                                textAlign: "left",
                              }}
                            >
                              {row?.[header.key]}
                            </span>
                          </td>
                        );
                      }

                      const cellContent = row[header.key];
                      return (
                        <td
                          key={header.key}
                          style={{
                            padding: header.isNumeric
                              ? "8px 20px 8px 8px !important"
                              : "8px",
                            textAlign: header.isNumeric ? "center" : "left",
                          }}
                        >
                          <TruncateWithTooltip
                            style={{
                              color: getColorByColumn(header.key, cellContent),
                            }}
                          >
                            {cellContent}
                          </TruncateWithTooltip>
                        </td>
                      );
                    })}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={headers?.length} style={{ textAlign: "center" }}>
                    <span className="no_rec_data">
                      {" "}
                      {isTodayAppointmentTable
                        ? "No Appointments Scheduled for Today"
                        : isUpcomingAppointmentTable
                          ? "No Appointments Scheduled"
                          : isPastAppointmentTable
                            ? "No Appointments"
                            : "No Record Found"}
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
      <SplInstructionsModal
        show={showModal}
        onClose={() => setShowModal(false)}
        data={instructionsData}
        position={modalPos}
      />
    </>
  );
};

export default DataTable;
